import React, { createContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import * as Sentry from '@sentry/react';

export type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'failed';

export const SocketIOContext = createContext<Socket>({} as Socket);
export const SocketStatusContext = createContext<ConnectionStatus>(
  'connecting'
);

export const useSocketStatus = () => React.useContext(SocketStatusContext);

interface Props {
  children: React.ReactNode;
}

export const SocketIOContextProvider = ({ children }: Props) => {
  const socketRef = useRef<Socket>();

  if (!socketRef.current) {
    socketRef.current = io(
      process.env.REACT_APP_SERVER_URL || 'localhost:1234'
    );
  }

  const socket = socketRef.current;
  const [status, setStatus] = useState<ConnectionStatus>(
    socket.connected ? 'connected' : 'connecting'
  );

  useEffect(() => {
    const sentryEnabled = Boolean(process.env.REACT_APP_SENTRY_DSN);
    const configuredUrl = process.env.REACT_APP_SERVER_URL || 'localhost:1234';
    let serverHost = 'unknown';

    try {
      const url = configuredUrl.includes('://')
        ? configuredUrl
        : `http://${configuredUrl}`;
      serverHost = new URL(url).host;
    } catch {
      // Keep diagnostics free of malformed URLs and query-string contents.
    }

    const addSocketBreadcrumb = (message: string) => {
      if (sentryEnabled) {
        Sentry.addBreadcrumb({ category: 'socket', message });
      }
    };

    const captureSocketFailure = (event: string) => {
      if (sentryEnabled) {
        Sentry.captureMessage(
          `socket ${event} (host: ${serverHost})`,
          Sentry.Severity.Warning
        );
      }
    };

    const handleConnect = () => {
      setStatus('connected');
      addSocketBreadcrumb('socket connected');
    };
    const handleDisconnect = (reason: string) => {
      setStatus('reconnecting');
      addSocketBreadcrumb(`socket disconnected (reason: ${reason})`);
    };
    const handleReconnecting = () => {
      setStatus('reconnecting');
      addSocketBreadcrumb('socket reconnecting');
    };
    const handleConnectError = () => {
      setStatus('reconnecting');
      addSocketBreadcrumb('socket connect_error');
    };
    const handleReconnectFailed = () => {
      setStatus('failed');
      addSocketBreadcrumb('socket reconnect_failed');
      captureSocketFailure('reconnect_failed');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.io.on('reconnect_attempt', handleReconnecting);
    socket.io.on('reconnect_failed', handleReconnectFailed);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.io.off('reconnect_attempt', handleReconnecting);
      socket.io.off('reconnect_failed', handleReconnectFailed);
    };
  }, [socket]);

  return (
    <SocketIOContext.Provider value={socket}>
      <SocketStatusContext.Provider value={status}>
        {children}
      </SocketStatusContext.Provider>
    </SocketIOContext.Provider>
  );
};
